import React, { FC, useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { Helmet } from "react-helmet-async";

import {
  getLoginLinkEmail,
  isLoginLink,
  sendLoginLink,
  signInWithLoginLink,
  signUserIn,
  signUserInWithToken,
} from "../../services/firebase";
import { POST_LOGIN_PATH_KEY } from "../../services/extension";
import {
  extensibilitySdkInitializedAtom,
  loginFormAtom,
  outreachOrgIdAtom,
} from "../../state/atoms";
import { canSubmitLoginFormSelector } from "../../state/selectors";
import { Errors } from "../../types/errors";
import { useAuthCrm, withCrm } from "../../components/CrmUi/authCrm";
import Loading from "../../components/Display/Loading";
import {
  AuthButton,
  AuthError,
  AuthField,
  AuthLink,
  AuthPanel,
  AuthShell,
  OnboardingPanel,
  AuthTextButton,
  AuthTitle,
  PencilIcon,
} from "./AuthUi";

/**
 * Sign in, in the look of the CRM the person came for (authCrm.ts): HubSpot's
 * app.hubspot.com/login or Outreach's accounts.outreach.io/users/sign_in.
 * Email first, then the password, as both ask; or a sign-in link by email.
 */
const Login: FC = () => {
  const crm = useAuthCrm();
  const isHubSpot = crm === "hubspot";
  const [loginForm, setLoginForm] = useRecoilState(loginFormAtom);
  const canSubmitLoginForm = useRecoilValue(canSubmitLoginFormSelector);
  const isExtensibilitySdkInitialized = useRecoilValue(
    extensibilitySdkInitializedAtom
  );
  const outreachOrgId = useRecoilValue(outreachOrgIdAtom);

  const [isProcessing, setIsProcessing] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [error, setError] = useState("");
  // Email first, then the password, the way Outreach's and HubSpot's sign in ask.
  const [step, setStep] = useState<"email" | "password">("email");
  const passwordRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // The address a sign-in link just went to, while "check your email" shows.
  const [linkSentTo, setLinkSentTo] = useState<string | null>(null);
  // Opened from a sign-in link in a browser that doesn't know which email it
  // was sent to, so it has to ask before it can finish.
  const [isConfirmingLinkEmail, setIsConfirmingLinkEmail] = useState(false);

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginForm.email.trim());

  // Inside Outreach (the extensibility SDK), sign up opens in a popup that
  // hands back a token when the account is made.
  const isSignupPopup = isExtensibilitySdkInitialized && !!outreachOrgId;

  const openSignupPopup = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const width = 800;
    const height = 800;

    // Centre the window over this one.
    const left = (window.innerWidth - width) / 2 + window.screenX;
    const top = (window.innerHeight - height) / 2 + window.screenY;

    const popup = window.open(
      `/auth/signup?popup=true&outreachOrganizationId=${outreachOrgId}&crm=outreach`,
      "popup",
      `width=${width},height=${height},scrollbars=yes,resizable=yes,top=${top},left=${left}`
    );
    setIsPopupOpen(true);

    window.addEventListener("message", (event) => {
      if (event.origin !== window.location.origin) {
        console.error("Message from untrusted origin:", event.origin);
        return;
      }

      const { type, token } = event.data || {};

      if (type && type === "close" && token) {
        signUserInWithToken(token);
        setIsPopupOpen(false);
        return;
      }

      setIsPopupOpen(false);
    });

    const checkPopupClosed = setInterval(() => {
      if (popup && popup.closed) {
        clearInterval(checkPopupClosed);
        setIsPopupOpen(false);
      }
    }, 500);
  };

  const signupLink = (label: string, ink?: boolean) => (
    <AuthLink
      crm={crm}
      to="/auth/signup"
      ink={ink}
      onClick={isSignupPopup ? openSignupPopup : undefined}
    >
      {label}
    </AuthLink>
  );

  const finishLinkSignIn = async (email: string) => {
    try {
      setIsProcessing(true);
      setError("");
      await signInWithLoginLink(email, window.location.href);
    } catch (err) {
      const error = err as { code?: string } | null;
      setError(
        error?.code === "auth/invalid-email"
          ? "That isn't the email this link was sent to."
          : "This sign-in link has expired or was already used. Send yourself a new one."
      );
      setIsConfirmingLinkEmail(error?.code === "auth/invalid-email");
      if (error?.code !== "auth/invalid-email") {
        window.history.replaceState(null, "", withCrm("/auth/login", crm));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (!isLoginLink(window.location.href)) return;

    // The link opens in a new tab, so carry where sign-in should land
    // (connect the extension, an AI app's consent page) over from the URL.
    const next = new URLSearchParams(window.location.search).get("next");
    if (next?.startsWith("/") && !next.startsWith("//")) {
      sessionStorage.setItem(POST_LOGIN_PATH_KEY, next);
    }

    const email = getLoginLinkEmail();
    if (email) {
      finishLinkSignIn(email);
    } else {
      setIsConfirmingLinkEmail(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSendLink = async () => {
    const email = loginForm.email.trim();
    if (!isEmailValid) return;

    try {
      setIsProcessing(true);
      setError("");
      await sendLoginLink(email, sessionStorage.getItem(POST_LOGIN_PATH_KEY) || undefined);
      setLinkSentTo(email);
    } catch (error) {
      setError("Couldn't send a sign-in link. Check the email address and try again.");
    } finally {
      setIsProcessing(false);
    }
  };

  const backToEmail = () => {
    setStep("email");
    setError("");
    setTimeout(() => emailRef.current?.focus(), 0);
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isConfirmingLinkEmail) {
      if (isEmailValid) finishLinkSignIn(loginForm.email.trim());
      return;
    }

    if (step === "email") {
      if (isEmailValid) {
        setError("");
        setStep("password");
        setTimeout(() => passwordRef.current?.focus(), 0);
      }
      return;
    }

    if (canSubmitLoginForm) {
      try {
        setIsProcessing(true);
        setError("");
        const { user } = await signUserIn(loginForm.email, loginForm.password);

        if (!user) {
          setError(
            `Error logging into account (Code ${Errors.AUTH_LOGIN_ERROR}). Please validate the data you have entered, and try again.`
          );

          return;
        }
      } catch (error) {
        setError(
          `Error logging into account (Code ${Errors.AUTH_LOGIN_FAILURE}). Please validate the data you have entered, and try again.`
        );
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const setEmail = (email: string) => setLoginForm({ ...loginForm, email });
  const setPassword = (password: string) => setLoginForm({ ...loginForm, password });

  let body: React.ReactNode;

  if (isPopupOpen) {
    body = (
      <AuthPanel crm={crm} title="Sign up" intro="Please continue signing up in the popup window.">
        <Loading />
      </AuthPanel>
    );
  } else if (linkSentTo) {
    body = (
      <AuthPanel
        crm={crm}
        title="Check your email"
        intro={
          <>
            We sent a sign-in link to <strong>{linkSentTo}</strong>. Open it in this
            browser to sign in.
          </>
        }
      >
        <AuthButton secondary onClick={() => setLinkSentTo(null)}>
          Back to sign in
        </AuthButton>
      </AuthPanel>
    );
  } else if (isConfirmingLinkEmail) {
    body = (
      <AuthPanel
        crm={crm}
        title="Sign in"
        intro="Confirm the email address the sign-in link was sent to."
        onSubmit={onSubmit}
      >
        <AuthField
          crm={crm}
          label={isHubSpot ? "Email" : "Email address"}
          type="email"
          name="email"
          autoComplete="username"
          autoFocus
          value={loginForm.email}
          onChange={setEmail}
        />
        <AuthButton type="submit" loading={isProcessing} busyLabel="Signing in" disabled={!isEmailValid}>
          Sign in
        </AuthButton>
        <AuthError>{error}</AuthError>
      </AuthPanel>
    );
  } else if (isHubSpot) {
    // app.hubspot.com/login: "Sign in", "or create an account", the email,
    // then on the next step the email with a pencil to change it.
    body = (
      <>
        <AuthTitle>Sign in</AuthTitle>
        <p className="ba-alt">or {signupLink("create an account")}</p>
        {step === "password" && (
          <div className="ba-email-row">
            <span>{loginForm.email}</span>
            <button
              type="button"
              className="ba-iconbtn"
              aria-label="Use a different email"
              onClick={backToEmail}
            >
              <PencilIcon />
            </button>
          </div>
        )}
        <form className="ba-form" onSubmit={onSubmit} noValidate>
          {step === "email" ? (
            <AuthField
              crm={crm}
              label="Email"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              placeholder="name@company.com"
              inputRef={emailRef}
              value={loginForm.email}
              onChange={setEmail}
            />
          ) : (
            <>
              {/* Kept for password managers, which pair the password with it. */}
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={loginForm.email}
                readOnly
                hidden
              />
              <AuthField
                crm={crm}
                label="Password"
                type="password"
                name="password"
                autoComplete="current-password"
                inputRef={passwordRef}
                value={loginForm.password}
                onChange={setPassword}
              />
            </>
          )}
          <AuthButton
            type="submit"
            loading={isProcessing}
            busyLabel="Signing in"
            disabled={step === "email" ? !isEmailValid : !canSubmitLoginForm}
          >
            {step === "email" ? "Next" : "Sign in"}
          </AuthButton>
          {step === "password" && (
            <>
              <AuthTextButton onClick={onSendLink} disabled={isProcessing}>
                Email me a sign-in link instead
              </AuthTextButton>
              <p className="ba-switch">
                <AuthLink crm={crm} to="/auth/reset-password">
                  Forgot your password?
                </AuthLink>
              </p>
            </>
          )}
          <AuthError>{error}</AuthError>
        </form>
      </>
    );
  } else {
    body = (
      <form className="ba-form" onSubmit={onSubmit} noValidate>
        <AuthTitle>Sign in</AuthTitle>
        <AuthField
          crm={crm}
          label="Email address"
          type="email"
          name="email"
          autoComplete="username"
          autoFocus
          inputRef={emailRef}
          value={loginForm.email}
          readOnly={step === "password"}
          onClick={() => step === "password" && setStep("email")}
          onChange={setEmail}
        />
        {step === "password" && (
          <AuthField
            crm={crm}
            label="Password"
            type="password"
            name="password"
            autoComplete="current-password"
            inputRef={passwordRef}
            value={loginForm.password}
            onChange={setPassword}
          />
        )}
        <AuthButton
          type="submit"
          loading={isProcessing}
          busyLabel="Signing in"
          disabled={step === "email" ? !isEmailValid : !canSubmitLoginForm}
        >
          {step === "email" ? "Continue" : "Sign in"}
        </AuthButton>
        {step === "password" && (
          <AuthButton secondary disabled={isProcessing} onClick={onSendLink}>
            Email me a sign-in link
          </AuthButton>
        )}
        <AuthError>{error}</AuthError>
        <div className="ba-secondary">
          {signupLink("Sign up")}
          <AuthLink crm={crm} to="/auth/reset-password">
            Reset password
          </AuthLink>
        </div>
      </form>
    );
  }

  // The booking calendar beside the form, for a demo or an onboarding call.
  // Not inside Outreach's extensibility frame, which is too small for it.
  const aside = isSignupPopup ? undefined : (
    <OnboardingPanel crm={crm} title="Want a personalized demo or onboarding?" sub="Book some time." />
  );

  return (
    <AuthShell crm={crm} aside={aside} illustration>
      <Helmet>
        <title>Sign in | boring</title>
      </Helmet>
      {body}
    </AuthShell>
  );
};

export default Login;
